import { useEffect, useState } from "react";

function getCSSVariable(name: string, from?: HTMLElement): string {
  if (typeof window === "undefined") {
    return "";
  } // Handle SSR
  const element = from || document.body;
  return getComputedStyle(element).getPropertyValue(name).trim();
}

type CSSVariableMap<T extends Record<string, string>> = {
  [K in keyof T]: string;
};

export function useCSSVariables<T extends Record<string, string>>(
  variableMap: T,
): CSSVariableMap<T> {
  const [colors, setColors] = useState<CSSVariableMap<T>>(() => {
    const initial: Partial<CSSVariableMap<T>> = {};
    for (const [key, variable] of Object.entries(variableMap)) {
      initial[key as keyof T] = getCSSVariable(variable);
    }
    return initial as CSSVariableMap<T>;
  });

  useEffect(() => {
    // Function to update colors
    const updateColors = () => {
      const newColors: Partial<CSSVariableMap<T>> = {};
      for (const [key, variable] of Object.entries(variableMap)) {
        newColors[key as keyof T] = getCSSVariable(variable);
      }
      setColors(newColors as CSSVariableMap<T>);
    };

    // Create an observer to watch for class changes on the body
    const observer = new MutationObserver(updateColors);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Initial color setup
    updateColors();

    // Cleanup
    return () => observer.disconnect();
  }, [Object.keys(variableMap).join(":")]);

  return colors;
}
